import React, { useState } from 'react';
import './App.css';

interface Room {
  year: number | string;
  title: string;
  image: string;
  note: string;
  locked?: boolean;
  letterImage: string;
  miniGame?: {
    type: 'word-find' | 'memory';
    data: any;
  };
}

const rooms: Room[] = [
  {
    year: 2018,
    title: "Where It All Began",
    image: "/2018.jpg", // Add your photos here
    note: "The moment I knew my life changed forever. Your smile, your laugh, the way you made everything feel like magic.",
    letterImage:'o.png',
    miniGame: {
      type: 'word-find',
      data: { words: ['FIRST', 'KISS', 'LOVE', 'SMILE'], sentence: "Our FIRST date, your beautiful SMILE, our first KISS, falling in LOVE" }
    }
  },
  {
    year: 2019,
    title: "Growing Together",
    image: "/2019_1.jpg",
    letterImage: 'u.png',
    note: "Learning your quirks, sharing dreams, and realizing that home isn't a place—it's you.",
  },
  {
    year: 2020,
    title: "COVID Hits, Love Grows",
    image: "/2020.jpg",
    letterImage: 'm.png',
    note: "World Stopped and but continued with countless inside jokes, and the way you make ordinary moments extraordinary.",
  },
  {
    year: 2021,
    title: "Through Thick and Thin",
    image: "/2021.jpg",
    letterImage: 'a.png',
    note: "Challenges continues together and came out stronger. You're my anchor and my wings.",
  },
  {
    year: 2022,
    title: "Deepening Love",
    image: "/2022.jpeg",
    letterImage: 'y.png',
    note: "Five years of loving you more each day. You've shown me what unconditional love really means.",
  },
  {
    year: 2023,
    title: "Long Distance Happens, But Love Endures",
    image: "/2023.png",
    letterImage: 'r_US.png',
    note: "Distance may separate us, but our love knows no bounds. Every call, every message, every moment apart only makes our hearts grow fonder.",
  },
  {
    year: '2024-2025',
    title: "Seven Years of Magic",
    image: "/2024.jpg",
    letterImage: 'r_final.png',
    note: "And now, after seven beautiful years, Here we are still going strong. Every moment with you is a treasure, and I can't wait to see what the future holds for us.",
  },
  {
    year: '',
    title: "...",
    letterImage: '',
    image: "/images/coming-soon.jpg",
    note: "Something magical",
    locked: true
  }
];

const App: React.FC = () => {
  const [currentRoom, setCurrentRoom] = useState<number | null>(null);
  const [completedRooms, setCompletedRooms] = useState<number[]>([]);
  const [openingDoor, setOpeningDoor] = useState<number | null>(null);
  const [openDoors, setOpenDoors] = useState<number[]>([]);

  const openRoom = (roomIndex: number) => {
    // Check if the room is locked
    if (rooms[roomIndex].locked) {
      return; // Don't open locked doors
    }
    
    if (openDoors.includes(roomIndex)) {
      // If door is already open, close it
      setOpenDoors(openDoors.filter(door => door !== roomIndex));
    } else {
      // Open the door with animation
      setOpeningDoor(roomIndex);
      setTimeout(() => {
        setOpeningDoor(null);
        setOpenDoors([...openDoors, roomIndex]);
      }, 600);
    }
  };

  const openModal = (roomIndex: number) => {
    setCurrentRoom(roomIndex);
  };

  const closeRoom = () => {
    if (currentRoom !== null && !completedRooms.includes(currentRoom)) {
      setCompletedRooms([...completedRooms, currentRoom]);
    }
    setCurrentRoom(null);
  };

  const WordFindGame: React.FC<{ data: any }> = ({ data }) => {
    const [foundWords, setFoundWords] = useState<string[]>([]);
    
    const handleWordClick = (word: string) => {
      if (!foundWords.includes(word)) {
        setFoundWords([...foundWords, word]);
      }
    };

    return (
      <div className="mini-game">
        <h4>Find the hidden words from our story:</h4>
        <p className="game-sentence">{data.sentence}</p>
        <div className="word-list">
          {data.words.map((word: string) => (
            <button
              key={word}
              className={`word-button ${foundWords.includes(word) ? 'found' : ''}`}
              onClick={() => handleWordClick(word)}
            >
              {word}
            </button>
          ))}
        </div>
        {foundWords.length === data.words.length && (
          <p className="game-complete">💕 You found them all! Just like how you found your way to my heart.</p>
        )}
      </div>
    );
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>Seven Years Of Us</h1>
        <p>Each door holds a year of our journey together</p>
      </header>

      <div className="rooms-grid">
        {rooms.map((room, index) => (
          <div
            key={index}
            className={`room-door ${completedRooms.includes(index) ? 'completed' : ''} ${openingDoor === index ? 'opening' : ''} ${openDoors.includes(index) ? 'open' : ''} ${room.locked ? 'locked' : ''}`}
            onClick={() => openRoom(index)}
          >
            <div className="door-frame">
              <div className="door-content">
                <div className="door-number">{room.year}</div>
                <div className="door-title">{room.title}</div>
                {completedRooms.includes(index) && <div className="completed-heart">💕</div>}
                {room.locked && <div className="lock-icon">🔒</div>}
              </div>
              
              {/* Room content revealed when door opens - only for unlocked doors */}
              {!room.locked && (
                <div className="room-behind-door">
                  <div className="room-preview">
                    <h3>{room.title}</h3>
                    <div className="room-image-preview">
                      <img src={room.image} alt={`Year ${room.year}`} />
                    </div>
                    <p className="room-note-preview">{room.note}</p>
                    <button 
                      className="enter-room-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(index);
                      }}
                    >
                      Read
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {currentRoom !== null && (
        <div className="room-modal" onClick={closeRoom}>
          <div className="room-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closeRoom}>×</button>
            
            <div className="room-inner">
              <h2>Year {rooms[currentRoom].year}: {rooms[currentRoom].title}</h2>
              
              <div className="room-image">
                <img src={rooms[currentRoom].image} alt={`Year ${rooms[currentRoom].year}`} />
              </div>
              
              <div className="room-note">
                <p>{rooms[currentRoom].note}</p>
              </div>
              {rooms[currentRoom].letterImage && (
                <div className="room-letter-image">
                  <img src={'/letters/'+rooms[currentRoom].letterImage} alt="Letter" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;